import { normalizeNavOrder } from '../constants/departmentWorkflow';

const STORAGE_KEY = 'navOrderPreference';

/**
 * Fired after a save so the sidebar updates without a reload. The browser's
 * own 'storage' event only reaches *other* tabs, so same-tab edits need this.
 */
export const NAV_ORDER_CHANGED_EVENT = 'navOrderPreferenceChanged';

const announce = () => {
  try {
    window.dispatchEvent(new Event(NAV_ORDER_CHANGED_EVENT));
  } catch {
    /* ignore */
  }
};

/**
 * A person's own sidebar order.
 *
 * Deliberately local to the device rather than server-side: this is a personal
 * preference, not configuration. It reorders only — it can never reveal an
 * item the department hid or a route the role doesn't grant, because the
 * sidebar applies it as the last step over an already-filtered list.
 *
 * Stored per department view, so someone who switches departments keeps a
 * sensible order in each rather than dragging one arrangement across all.
 */
const keyFor = (department) => `${STORAGE_KEY}:${department || 'default'}`;

export function getStoredNavOrder(department) {
  try {
    const raw = localStorage.getItem(keyFor(department));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    // Normalising on read self-heals an order saved before a menu item existed.
    return normalizeNavOrder(parsed);
  } catch {
    return null;
  }
}

export function setStoredNavOrder(department, order) {
  try {
    if (!Array.isArray(order) || order.length === 0) {
      localStorage.removeItem(keyFor(department));
      announce();
      return;
    }
    localStorage.setItem(keyFor(department), JSON.stringify(normalizeNavOrder(order)));
    announce();
  } catch {
    /* ignore — a full or blocked store just means no saved order */
  }
}

export function clearStoredNavOrder(department) {
  try {
    localStorage.removeItem(keyFor(department));
    announce();
  } catch {
    /* ignore */
  }
}
